import type React from "react";
import {
  BookOpen,
  Brain,
  CloudRain,
  GitBranch,
  Heart,
  Lightbulb,
  Smile,
  Target,
  UserRound,
} from "lucide-react";

/**
 * One look per mental-model type (spec 2026-11-03 model page §B): the icon and
 * the tints every model row, chip, Underneath chip, Connected chip and the
 * model page header use. Light and dark variants live in the class strings.
 */
export interface MmTypeStyle {
  icon: React.ElementType;
  /** Square icon tile (rows, page header). */
  tile: string;
  /** Rounded chip (Underneath, Connected). */
  chip: string;
  /** Tinted text / icon colour. */
  text: string;
}

export const MM_TYPE_STYLES: Record<string, MmTypeStyle> = {
  fear: {
    icon: CloudRain,
    tile: "bg-rose-50 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300",
    chip: "border-rose-200 bg-rose-50/70 text-rose-800 hover:border-rose-400 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200",
    text: "text-rose-600 dark:text-rose-300",
  },
  belief: {
    icon: Lightbulb,
    tile: "bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300",
    chip: "border-indigo-200 bg-indigo-50/70 text-indigo-800 hover:border-indigo-400 dark:border-indigo-500/30 dark:bg-indigo-500/10 dark:text-indigo-200",
    text: "text-indigo-600 dark:text-indigo-300",
  },
  goal: {
    icon: Target,
    tile: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
    chip: "border-emerald-200 bg-emerald-50/70 text-emerald-800 hover:border-emerald-400 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200",
    text: "text-emerald-700 dark:text-emerald-300",
  },
  decision: {
    icon: GitBranch,
    tile: "bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300",
    chip: "border-sky-200 bg-sky-50/70 text-sky-800 hover:border-sky-400 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200",
    text: "text-sky-700 dark:text-sky-300",
  },
  self_image: {
    icon: UserRound,
    tile: "bg-violet-50 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300",
    chip: "border-violet-200 bg-violet-50/70 text-violet-800 hover:border-violet-400 dark:border-violet-500/30 dark:bg-violet-500/10 dark:text-violet-200",
    text: "text-violet-600 dark:text-violet-300",
  },
  value: {
    icon: Heart,
    tile: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300",
    chip: "border-amber-200 bg-amber-50/70 text-amber-800 hover:border-amber-400 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200",
    text: "text-amber-700 dark:text-amber-300",
  },
  memory: {
    icon: BookOpen,
    tile: "bg-slate-100 text-slate-600 dark:bg-slate-500/20 dark:text-slate-300",
    chip: "border-slate-200 bg-slate-50/70 text-slate-700 hover:border-slate-400 dark:border-slate-500/30 dark:bg-slate-500/10 dark:text-slate-200",
    text: "text-slate-600 dark:text-slate-300",
  },
  attitude: {
    icon: Smile,
    tile: "bg-teal-50 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300",
    chip: "border-teal-200 bg-teal-50/70 text-teal-800 hover:border-teal-400 dark:border-teal-500/30 dark:bg-teal-500/10 dark:text-teal-200",
    text: "text-teal-700 dark:text-teal-300",
  },
  other: {
    icon: Brain,
    tile: "bg-muted text-muted-foreground",
    chip: "border-border bg-muted/50 text-foreground/80 hover:border-primary/30",
    text: "text-muted-foreground",
  },
};

export const mmTypeStyle = (type: string | undefined): MmTypeStyle =>
  (type && MM_TYPE_STYLES[type]) || MM_TYPE_STYLES.other;

/** i18n key of the capitalised type name ("Fear", "פחד", "Angst"). */
export const mmTypeNameKey = (type: string | undefined) => `capture.mmType.${type || "other"}`;
